/* The four seats: what each is called, where each lands, and what each sees.
 *
 * Ported from the demo's ROLE_META / ROLE_HOME / ROLE_NAV, which carry a lot of
 * decisions that are not obvious from the shape:
 *
 *   THE KEYS ARE NOT RENAMED. `sales` means SUPPORT COACH. The strings are checked by
 *   permission guards against live gym data, so a rename that misses one guard is a
 *   permissions bug on real members rather than a cosmetic one. Labels move; keys do
 *   not.
 *
 *   THE NAV IS ABOUT ATTENTION, NOT CAPABILITY. The Owner and Manager navs were cut
 *   back deliberately; everything removed is still reachable by drilling in. A screen
 *   leaving the sidebar is not the seat losing the screen.
 *
 *   THE SUPPORT COACH AND COACH NAVS HAVE NO HEADERS. Four and five items did not
 *   need grouping, and the headings were taller than the list.
 */
import type { Permission, Role } from '@acrostic/model';

export interface SeatMeta { label: string; rank: number }

/** Lower rank is more senior. Orders the nav when somebody holds several seats. */
export const SEATS: Record<string, SeatMeta> = {
  gym_admin: { label: 'Owner',         rank: 1 },
  manager:   { label: 'Manager',       rank: 2 },
  sales:     { label: 'Support Coach', rank: 3 },
  coach:     { label: 'Coach',         rank: 4 },
};

export const seatLabel = (r: string): string => SEATS[r]?.label ?? String(r).replace('_', ' ');

/** The screen a seat lands on. Decided by the user's FIRST role. */
export const SEAT_HOME: Record<string, string> = {
  gym_admin: 'dashboard',
  manager:   'home-manager',
  sales:     'home-sales',
  coach:     'home-coach',
};

export interface NavItem { name?: string; label: string; header?: true }

export const SEAT_NAV: Record<string, NavItem[]> = {
  gym_admin: [
    { label: 'Overview', header: true },
    { name: 'dashboard', label: 'Dashboard' },
    { name: 'approvals', label: 'Approvals' },
    /* OWNER ONLY. It is the one seat that may lead with money at all, and the only
       one that is shown what a member pays beside what they bring. */
    { name: 'worth', label: 'Worth & value' },
    { label: 'Grow', header: true },
    { name: 'leads', label: 'Leads' },
    { name: 'campaigns', label: 'Campaigns' },
    { label: 'Retain', header: true },
    { name: 'members', label: 'Members' },
    { name: 'classes', label: 'Classes' },
    { name: 'segments', label: 'Segments' },
    { label: 'Platform', header: true },
    { name: 'trainers', label: 'Coaches & Trainers' },
    { name: 'accounts', label: 'Staff accounts' },
    { name: 'settings', label: 'Settings' },
  ],
  manager: [
    { label: 'Overview', header: true },
    { name: 'home-manager', label: 'My Day' },
    { name: 'dashboard', label: 'Dashboard' },
    { label: 'Team', header: true },
    { name: 'leads', label: 'Leads' },
    { name: 'members', label: 'Members' },
    { name: 'classes', label: 'Classes' },
    { name: 'campaigns', label: 'Campaigns' },
    { name: 'segments', label: 'Segments' },
    { label: 'Manage', header: true },
    { name: 'trainers', label: 'Coaches & Trainers' },
    { name: 'accounts', label: 'Staff accounts' },
    { name: 'settings', label: 'Settings' },
  ],
  /* The floor comes before the pipeline: Classes sits above Leads and Members, because
     checking people in is its own job done at its own pace. */
  sales: [
    { name: 'home-sales', label: 'Dashboard' },
    { name: 'classes', label: 'Classes' },
    { name: 'leads', label: 'Leads' },
    { name: 'members', label: 'Members' },
  ],
  coach: [
    { name: 'home-coach', label: 'Dashboard' },
    { name: 'classes', label: "Today's Sessions" },
    { name: 'coach', label: 'My PT Clients' },
    { name: 'leads', label: 'Leads' },
    { name: 'members', label: 'Members' },
  ],
};

/** A user's roles, most senior first. */
export function seatsOf(roles: readonly Role[] | undefined): string[] {
  return [...(roles ?? [])].sort((a, b) => (SEATS[a]?.rank ?? 99) - (SEATS[b]?.rank ?? 99));
}

/** Where this user lands. Their FIRST role as listed, not their most senior. */
export function homeFor(roles: readonly Role[] | undefined): string {
  const first = roles?.[0];
  return (first && SEAT_HOME[first]) || 'home-sales';
}

/* WHICH SCREENS ARE A PERMISSION RATHER THAN A SEAT.
 *
 * Everything not named here is open to anybody who works at the gym - Members,
 * Classes, Leads, Segments - and the seat nav decides only whether it is worth their
 * attention. These five the server will actually refuse, so the nav has to agree with
 * it in BOTH directions: an item somebody cannot use is a dead link, and a permission
 * somebody was given with no way to reach it is a grant the Owner will think failed. */
const NEEDS: Record<string, Permission> = {
  approvals: 'approvals',
  worth: 'worth',
  campaigns: 'campaigns',
  accounts: 'accounts',
  settings: 'settings',
};

/** The label to use when a screen has to be added back for somebody. */
const ADDED_LABEL: Record<string, string> = {
  approvals: 'Approvals',
  worth: 'Worth & value',
  campaigns: 'Campaigns',
  accounts: 'Staff accounts',
  settings: 'Settings',
};

/** The union of a multi-seat user's nav, most senior seat first, de-duplicated. */
export function navFor(
  roles: readonly Role[] | undefined,
  permissions?: readonly Permission[],
): NavItem[] {
  const out: NavItem[] = [];
  const seen = new Set<string>();
  for (const r of seatsOf(roles)) {
    for (const item of SEAT_NAV[r] ?? []) {
      if (item.header) { out.push(item); continue; }
      if (item.name && seen.has(item.name)) continue;
      if (item.name) seen.add(item.name);
      out.push(item);
    }
  }
  /* A header whose whole group was de-duplicated away would be a heading over
     nothing. Keep a header only when at least one item follows it before the next
     header. Written as a walk rather than a filter with a lookahead inside it,
     because the filter version was already unreadable at three seats. */
  /* DROP WHAT THEY CANNOT USE. Done before the empty-header pass below, so a group
     whose every item was removed takes its heading with it. `permissions` undefined
     means "not told" - every caller passes it, and defaulting to hiding nothing keeps
     a missing argument from silently emptying somebody's sidebar. */
  const usable = permissions
    ? out.filter((i) => !i.name || !NEEDS[i.name] || permissions.includes(NEEDS[i.name]!))
    : out;

  const kept: NavItem[] = [];
  for (let i = 0; i < usable.length; i++) {
    const item = usable[i]!;
    if (!item.header) { kept.push(item); continue; }
    const next = usable[i + 1];
    if (next && !next.header) kept.push(item);
  }

  /* AND ADD BACK WHAT THEY WERE GIVEN. A coach handed `worth` has the screen and no
     way to reach it, and an Owner who granted it sees nothing change. The seat navs
     are curated on purpose - "the nav is about attention, not capability" - so these
     go in their own group at the end rather than being threaded into somebody else's
     idea of what matters. */
  if (permissions) {
    const already = new Set(kept.map((i) => i.name).filter(Boolean));
    const extra = Object.entries(NEEDS)
      .filter(([name, need]) => permissions.includes(need) && !already.has(name))
      .map(([name]) => ({ name, label: ADDED_LABEL[name] ?? name }));
    if (extra.length) {
      kept.push({ label: 'Also yours', header: true }, ...extra);
    }
  }
  return kept;
}

/* WHO MAY SEE WHAT A MEMBER PAYS.
 *
 * A Manager and an Owner run the business, so they see plans, prices and renewals.
 * A Coach and a Support Coach do not, by default, and that default is a product
 * decision rather than a permissions detail: a coach who knows this member is on
 * 549 and that one is on 199 is a coach who treats them differently, and treating
 * every member the same is the whole argument.
 *
 * IT IS A DEFAULT, NOT A LAW. Gyms differ, and an owner who wants their coaches to
 * see the plan can say so on Settings. What must not happen is a gym discovering
 * the rule by accident, so the setting states the default and why.
 *
 * It is enforced in the SOURCE, which redacts the three fields before a floor seat
 * is ever handed a member, so a screen cannot show what it was never sent. The
 * screens read this too, but only to drop the furniture: a Plan column of dashes is
 * worse than no Plan column. The refusal is the source's; this is the courtesy.
 */

/** The seats that see commercials unless the gym says otherwise. */
/* The seat rules live in @acrostic/model, because the API applies the same ones and
   the two copies of seesCommercials had already been written. Re-exported here so the
   screens keep importing from seats.ts, which is where they look for anything about a
   seat. */
export {
  GYM_ROLES, CAN_SET_VISIBILITY, COMMERCIALS_DEFAULT, canSetVisibility,
  grantableBy, seesCommercials,
} from '@acrostic/model';

